
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface AdminStatistics {
  totalUsers: number;
  activeProperties: number;
  activeSubscriptions: number;
  monthlyRequests: number;
  totalLandlords: number;
  totalTenants: number;
  totalServiceProviders: number;
  monthlyRevenue: number;
  propertyTypeDistribution: Array<{ name: string; value: number; }>;
  monthlyGrowthData: Array<{ month: string; properties: number; users: number; revenue: number; }>;
  topLocations: Array<{ location: string; count: number; }>;
}

export const useAdminStatistics = () => {
  const [statistics, setStatistics] = useState<AdminStatistics | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStatistics();
  }, []);

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      
      // جلب العدد الإجمالي للملاك
      const { count: landlordsCount } = await supabase
        .from('landlords')
        .select('*', { count: 'exact', head: true });

      // جلب العدد الإجمالي للباحثين
      const { count: tenantsCount } = await supabase
        .from('tenants')
        .select('*', { count: 'exact', head: true });

      // جلب العدد الإجمالي لأصحاب الخدمات
      const { count: serviceProvidersCount } = await supabase
        .from('service_providers')
        .select('*', { count: 'exact', head: true });

      // جلب العقارات النشطة
      const { count: activePropertiesCount } = await supabase
        .from('properties')
        .select('*', { count: 'exact', head: true })
        .eq('is_active', true);

      // جلب الاشتراكات النشطة
      const { count: activeSubscriptionsCount } = await supabase
        .from('subscriptions')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'active');

      // جلب طلبات هذا الشهر
      const currentMonth = new Date();
      currentMonth.setDate(1);
      const { count: monthlyRequestsCount } = await supabase
        .from('property_management_requests')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', currentMonth.toISOString());

      // حساب الإيرادات الشهرية
      const { data: subscriptionsData } = await supabase
        .from('subscriptions')
        .select('price, created_at')
        .eq('status', 'active')
        .gte('created_at', currentMonth.toISOString());

      const monthlyRevenue = subscriptionsData?.reduce((sum, sub) => sum + Number(sub.price), 0) || 0;

      // جلب توزيع أنواع العقارات
      const { data: propertiesData } = await supabase
        .from('properties')
        .select('type')
        .eq('is_active', true);

      const propertyTypes = propertiesData?.reduce((acc, property) => {
        acc[property.type] = (acc[property.type] || 0) + 1;
        return acc;
      }, {} as Record<string, number>) || {};

      const propertyTypeDistribution = Object.entries(propertyTypes).map(([name, value]) => ({
        name: name === 'apartment' ? 'شقة' : name === 'villa' ? 'فيلا' : name === 'office' ? 'مكتب' : name,
        value
      }));

      // جلب أفضل المواقع
      const { data: locationsData } = await supabase
        .from('properties')
        .select('address')
        .eq('is_active', true);

      const locations = locationsData?.reduce((acc, property) => {
        if (property.address) {
          const city = property.address.split(',')[0] || property.address;
          acc[city] = (acc[city] || 0) + 1;
        }
        return acc;
      }, {} as Record<string, number>) || {};

      const topLocations = Object.entries(locations)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 5)
        .map(([location, count]) => ({ location, count }));

      // بيانات النمو الشهري (آخر 6 أشهر)
      const monthlyGrowthData = [];
      const monthNames = [
        'يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو',
        'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'
      ];

      for (let i = 5; i >= 0; i--) {
        const date = new Date();
        date.setMonth(date.getMonth() - i);
        const startOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
        const endOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0);

        const { count: monthProperties } = await supabase
          .from('properties')
          .select('*', { count: 'exact', head: true })
          .gte('created_at', startOfMonth.toISOString())
          .lte('created_at', endOfMonth.toISOString());

        const { count: monthUsers } = await supabase
          .from('landlords')
          .select('*', { count: 'exact', head: true })
          .gte('created_at', startOfMonth.toISOString())
          .lte('created_at', endOfMonth.toISOString());

        const { data: monthSubscriptions } = await supabase
          .from('subscriptions')
          .select('price')
          .gte('created_at', startOfMonth.toISOString())
          .lte('created_at', endOfMonth.toISOString());

        const monthRevenue = monthSubscriptions?.reduce((sum, sub) => sum + Number(sub.price), 0) || 0;

        monthlyGrowthData.push({
          month: monthNames[date.getMonth()],
          properties: monthProperties || 0,
          users: monthUsers || 0,
          revenue: monthRevenue
        });
      }

      setStatistics({
        totalUsers: (landlordsCount || 0) + (tenantsCount || 0) + (serviceProvidersCount || 0),
        totalLandlords: landlordsCount || 0,
        totalTenants: tenantsCount || 0,
        totalServiceProviders: serviceProvidersCount || 0,
        activeProperties: activePropertiesCount || 0,
        activeSubscriptions: activeSubscriptionsCount || 0,
        monthlyRequests: monthlyRequestsCount || 0,
        monthlyRevenue,
        propertyTypeDistribution,
        monthlyGrowthData,
        topLocations
      });

    } catch (error) {
      console.error('Error fetching admin statistics:', error);
    } finally {
      setLoading(false);
    }
  };

  return {
    statistics,
    loading,
    refetch: fetchStatistics
  };
};
